import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryDropdown from '@/components/feature/CategoryDropdown';
import SeoSchema from '@/components/feature/SeoSchema';
import { useState } from 'react';
import { articles } from '@/mocks/articles';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://itaobuyspread.skin';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [categoryOpen, setCategoryOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar onCategoryClick={() => setCategoryOpen(true)} />
        <CategoryDropdown isOpen={categoryOpen} onClose={() => setCategoryOpen(false)} />
        <main className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-10 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-6">
              <i className="ri-error-warning-line text-3xl text-accent-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-foreground-950 font-heading mb-4">Articolo non trovato</h1>
            <p className="text-foreground-600 font-body mb-8">L&apos;articolo che stai cercando non è disponibile.</p>
            <a href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap font-label">
              <i className="ri-arrow-left-line"></i>
              Torna alla Home
            </a>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentSections = getContentSections(article.content);

  const articleUrl = `${siteUrl}/articolo/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    url: articleUrl,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'Itaobuy Spreadsheet Italia',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy Spreadsheet Italia',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'it',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `${siteUrl}/#articoli`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: articleUrl,
      },
    ],
  };

  return (
    <>
      <SeoSchema data={articleSchema} />
      <SeoSchema data={breadcrumbSchema} />
      <Navbar onCategoryClick={() => setCategoryOpen(true)} />
      <CategoryDropdown isOpen={categoryOpen} onClose={() => setCategoryOpen(false)} />

      <main>
        <article>
          <header className="relative w-full pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-10">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 font-body mb-6">
                <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-400">{article.category}</span>
              </nav>

              <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-700 text-xs font-medium rounded-full font-label mb-4">
                {article.category}
              </span>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 font-heading leading-tight">
                {article.title}
              </h1>

              <p className="mt-4 text-base md:text-lg text-foreground-600 font-body leading-relaxed max-w-3xl">
                {article.excerpt}
              </p>

              <div className="mt-6 flex items-center gap-4 text-xs text-foreground-500 font-body">
                <span>Aggiornato: {new Date(article.date).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>Tempo di lettura: {article.readTime}</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="rounded-3xl overflow-hidden h-64 md:h-96 mb-12">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10 pb-20">
            {contentSections}

            <div className="mt-16 border-t border-background-200/60 pt-10">
              <div className="flex flex-col sm:flex-row items-center gap-4 p-6 bg-primary-50 rounded-2xl">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-primary-600 text-xl"></i>
                  </div>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-sm font-semibold text-foreground-900 font-label">
                    Pronto a provare Itaobuy Sheet?
                  </p>
                  <p className="text-xs text-foreground-600 font-body mt-1">
                    Visita il sito ufficiale per iniziare subito.
                  </p>
                </div>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 font-label"
                >
                  Vai al Sito
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="mt-8 p-6 bg-background-100 rounded-2xl text-center">
              <p className="text-sm text-foreground-600 font-body mb-3">
                Torna alla pagina principale per esplorare altre guide e risorse.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors font-label"
              >
                <i className="ri-arrow-left-line"></i>
                Homepage
              </a>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}

function getContentSections(contentKey: string) {
  switch (contentKey) {
    case 'sicurezza':
      return <SicurezzaContent />;
    case 'tutorial':
      return <TutorialContent />;
    case 'recensione':
      return <RecensioneContent />;
    case 'vs-excel':
      return <VsExcelContent />;
    case 'alternative':
      return <AlternativeContent />;
    case 'come-iniziare':
      return <ComeIniziareContent />;
    case 'vantaggi':
      return <VantaggiContent />;
    case 'prezzo':
      return <PrezzoContent />;
    case 'template':
      return <TemplateContent />;
    case 'guida-acquisto':
      return <GuidaAcquistoContent />;
    default:
      return <GuidaCompletaContent />;
  }
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-5">{children}</h2>;
}

function TextParagraph({ children }: { children: React.ReactNode }) {
  return <p className="text-base text-foreground-700 font-body leading-relaxed mb-4">{children}</p>;
}

function GuidaCompletaContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Cos&apos;è Itaobuy Sheet?</SectionHeading>
        <TextParagraph>
          <strong>Itaobuy sheet</strong> è una piattaforma innovativa che unisce le funzionalità di un foglio di calcolo avanzato con strumenti moderni per la gestione di dati e informazioni. Nata per semplificare il lavoro di chi gestisce grandi quantità di dati, itaobuy spreadsheet si distingue per la sua interfaccia intuitiva e le potenti capacità di automazione.
        </TextParagraph>
        <TextParagraph>
          A differenza dei tradizionali software per fogli di calcolo, itaobuy sheet è stato progettato pensando alle esigenze degli utenti moderni: accessibilità da qualsiasi dispositivo, collaborazione in tempo reale e una curva di apprendimento sorprendentemente bassa. Che tu sia un principiante o un utente esperto, troverai in itaobuy spreadsheet uno strumento versatile e potente.
        </TextParagraph>
      </section>

      <section className="mb-12">
        <SectionHeading>I Vantaggi di Itaobuy Spreadsheet</SectionHeading>
        <div className="space-y-6">
          {[
            { icon: 'ri-speed-up-line', title: 'Interfaccia Intuitiva', desc: 'Comandi logici, funzioni principali a portata di clic, esperienza fluida anche con grandi volumi di dati.' },
            { icon: 'ri-cloud-line', title: 'Accesso Cloud', desc: 'Dati sempre accessibili da qualsiasi dispositivo. Collaborazione in tempo reale con modifiche visibili istantaneamente.' },
            { icon: 'ri-tools-line', title: 'Automazioni Intelligenti', desc: 'Flussi di lavoro automatici che eliminano attività ripetitive. Template predefiniti per le esigenze più comuni.' },
            { icon: 'ri-shield-check-line', title: 'Sicurezza Avanzata', desc: 'Crittografia TLS 1.3 e AES-256, backup automatici, autenticazione a due fattori e controlli di accesso granulari.' },
          ].map((item) => (
            <div key={item.title} className="bg-background-50 rounded-2xl border border-background-200/60 p-6">
              <h3 className="text-lg font-semibold text-foreground-900 font-label flex items-center gap-2">
                <i className={`${item.icon} text-primary-500`}></i>
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 font-body leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <SectionHeading>Come Iniziare</SectionHeading>
        <TextParagraph>Iniziare con itaobuy spreadsheet è semplice. Ecco i passaggi fondamentali:</TextParagraph>
        <ol className="space-y-4 list-decimal list-inside">
          {['Crea il tuo account in pochi minuti', 'Sfoglia i template disponibili', 'Importa dati da CSV, Excel o Google Sheets', 'Personalizza il foglio con formule e automazioni', 'Invita collaboratori e imposta i permessi'].map((step, i) => (
            <li key={i} className="text-base text-foreground-700 font-body leading-relaxed"><strong>Passo {i + 1}:</strong> {step}</li>
          ))}
        </ol>
      </section>
    </>
  );
}

function SicurezzaContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Itaobuy Sheet è Sicuro? La Nostra Analisi</SectionHeading>
        <TextParagraph>La sicurezza dei dati è una delle principali preoccupazioni per chi utilizza piattaforme digitali. Con <strong>itaobuy sheet</strong>, puoi stare tranquillo: la piattaforma adotta standard di sicurezza di livello enterprise. In questa guida analizziamo ogni aspetto della sicurezza di itaobuy spreadsheet.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Crittografia e Protezione Dati</SectionHeading>
        <TextParagraph>Itaobuy sheet utilizza crittografia TLS 1.3 per tutti i dati in transito e AES-256 per i dati a riposo. Questo significa che le tue informazioni sono protette sia durante il trasferimento che quando vengono archiviate sui server. È lo stesso livello di sicurezza utilizzato dalle banche e dalle istituzioni finanziarie.</TextParagraph>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {[
            { icon: 'ri-lock-line', title: 'Crittografia End-to-End', desc: 'Tutti i dati vengono cifrati prima di lasciare il tuo dispositivo.' },
            { icon: 'ri-shield-flash-line', title: 'Protezione DDoS', desc: 'Sistemi automatici di rilevamento e mitigazione degli attacchi.' },
            { icon: 'ri-fingerprint-line', title: '2FA', desc: 'Autenticazione a due fattori per un accesso più sicuro al tuo account.' },
            { icon: 'ri-server-line', title: 'Backup Automatici', desc: 'Backup regolari su server ridondanti in diverse località geografiche.' },
          ].map((item) => (
            <div key={item.title} className="bg-background-100 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-accent-600`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 font-label">{item.title}</h3>
              </div>
              <p className="text-xs text-foreground-600 font-body leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="mb-12">
        <SectionHeading>Buone Pratiche di Sicurezza</SectionHeading>
        <ul className="space-y-3 list-disc list-inside">
          {['Usa sempre password complesse e uniche', 'Attiva l\'autenticazione a due fattori', 'Non condividere le credenziali con nessuno', 'Esporta regolarmente un backup locale dei dati importanti', 'Verifica periodicamente i dispositivi collegati al tuo account'].map((t, i) => (
            <li key={i} className="text-base text-foreground-700 font-body leading-relaxed">{t}</li>
          ))}
        </ul>
      </section>
    </>
  );
}

function TutorialContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Tutorial Completo: Come Usare Itaobuy Sheet</SectionHeading>
        <TextParagraph>Benvenuto nel tutorial italiano completo su <strong>itaobuy sheet</strong>. In questa guida passo dopo passo, ti mostreremo come utilizzare tutte le funzionalità di itaobuy spreadsheet, dalla registrazione iniziale fino alle automazioni avanzate.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Passo 1: Registrazione e Configurazione</SectionHeading>
        <TextParagraph>Il primo passo è creare il tuo account su itaobuy sheet. Il processo di registrazione richiede solo pochi minuti: inserisci la tua email, scegli una password sicura e verifica il tuo indirizzo email. Una volta completata la registrazione, avrai accesso alla dashboard principale dove potrai iniziare a creare i tuoi primi fogli di calcolo.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Passo 2: Creazione del Primo Foglio</SectionHeading>
        <TextParagraph>Dalla dashboard, clicca su &quot;Nuovo Foglio&quot;. Puoi scegliere tra un foglio vuoto o uno dei tanti template disponibili. Per i principianti, consigliamo di partire con un template: ti farà risparmiare tempo e ti mostrerà le potenzialità della piattaforma. I template coprono esigenze come gestione inventario, budget personali, report aziendali e molto altro.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Passo 3: Formule e Automazioni</SectionHeading>
        <TextParagraph>Itaobuy sheet supporta tutte le formule standard dei fogli di calcolo, più alcune esclusive. Puoi creare automazioni per attività ripetitive come l&apos;invio di report settimanali, l&apos;aggiornamento automatico dei dati e la sincronizzazione con altre piattaforme. Le automazioni si configurano con un sistema visuale intuitivo, senza bisogno di scrivere codice.</TextParagraph>
      </section>
    </>
  );
}

function RecensioneContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Recensione Completa di Itaobuy Sheet</SectionHeading>
        <TextParagraph>Dopo aver testato approfonditamente <strong>itaobuy sheet</strong> per diverse settimane, siamo pronti a condividere la nostra recensione completa. In questo articolo analizziamo pro, contro, funzionalità e qualità complessiva di itaobuy spreadsheet.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Pro di Itaobuy Sheet</SectionHeading>
        <div className="space-y-4">
          {['Interfaccia pulita e intuitiva, adatta anche ai principianti', 'Template professionali pronti all\'uso', 'Automazioni potenti senza bisogno di codice', 'Collaborazione in tempo reale fluida', 'Assistenza clienti reattiva e disponibile'].map((pro, i) => (
            <div key={i} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 flex-shrink-0"></i>
              <span className="text-sm text-foreground-700 font-body">{pro}</span>
            </div>
          ))}
        </div>
      </section>
      <section className="mb-12">
        <SectionHeading>Contro di Itaobuy Sheet</SectionHeading>
        <div className="space-y-4">
          {['Alcune funzionalità avanzate richiedono il piano premium', 'La versione mobile potrebbe essere migliorata', 'Manca l\'integrazione nativa con alcuni software di terze parti'].map((con, i) => (
            <div key={i} className="flex items-start gap-3">
              <i className="ri-close-circle-fill text-red-400 mt-0.5 flex-shrink-0"></i>
              <span className="text-sm text-foreground-700 font-body">{con}</span>
            </div>
          ))}
        </div>
      </section>
      <section className="mb-12">
        <SectionHeading>Verdetto Finale</SectionHeading>
        <TextParagraph>Itaobuy sheet è un&apos;ottima soluzione per chi cerca un foglio di calcolo moderno, intuitivo e ricco di funzionalità. Nonostante qualche piccola limitazione, il rapporto qualità-prezzo è eccellente e lo consigliamo sia a principianti che a utenti esperti.</TextParagraph>
      </section>
    </>
  );
}

function VsExcelContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Itaobuy Sheet vs Excel: Il Confronto Definitivo</SectionHeading>
        <TextParagraph>Microsoft Excel è il re indiscusso dei fogli di calcolo da decenni. Ma <strong>itaobuy sheet</strong> sta guadagnando terreno grazie alle sue funzionalità moderne. In questo confronto analizziamo le differenze chiave per aiutarti a scegliere lo strumento migliore.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Prezzo e Accessibilità</SectionHeading>
        <TextParagraph>Excel richiede una licenza Microsoft 365 (a pagamento) per le funzionalità complete, mentre itaobuy sheet offre un piano gratuito generoso e piani premium più accessibili. Inoltre, itaobuy spreadsheet è completamente basato sul cloud, quindi non richiede installazione.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Funzionalità a Confronto</SectionHeading>
        <TextParagraph>Excel ha un vantaggio in termini di funzionalità avanzate come Power Query e VBA, ma itaobuy sheet eccelle nella collaborazione in tempo reale, nelle automazioni no-code e nell&apos;esperienza utente moderna. Per la maggior parte degli utenti, itaobuy spreadsheet offre tutto ciò di cui hanno bisogno con una curva di apprendimento molto più bassa.</TextParagraph>
      </section>
    </>
  );
}

function AlternativeContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Le Migliori Alternative a Itaobuy Sheet</SectionHeading>
        <TextParagraph>Sebbene <strong>itaobuy sheet</strong> sia un&apos;ottima soluzione, esistono valide alternative che potrebbero adattarsi meglio alle tue esigenze specifiche. Ecco una panoramica completa.</TextParagraph>
      </section>
      <section className="mb-12">
        <div className="space-y-6">
          {[
            { name: 'Google Sheets', desc: 'Gratuito, basato sul cloud, ottima collaborazione. Ideale per chi cerca una soluzione semplice e già integrata con Google Workspace.', stars: 4 },
            { name: 'Microsoft Excel', desc: 'Il più potente e completo. Perfetto per analisi dati avanzate e utenti professionali che necessitano di funzionalità come Power Query.', stars: 5 },
            { name: 'Airtable', desc: 'Unisce database e fogli di calcolo. Eccellente per la gestione di progetti e database relazionali con un\'interfaccia visiva moderna.', stars: 4 },
          ].map((alt) => (
            <div key={alt.name} className="bg-background-50 rounded-2xl border border-background-200/60 p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-foreground-900 font-label">{alt.name}</h3>
                <span className="text-yellow-500 text-sm">{'★'.repeat(alt.stars)}{'☆'.repeat(5 - alt.stars)}</span>
              </div>
              <p className="text-sm text-foreground-600 font-body leading-relaxed">{alt.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function ComeIniziareContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Itaobuy Sheet per Principianti: Da Zero a Esperto</SectionHeading>
        <TextParagraph>Non hai mai usato un foglio di calcolo prima d&apos;ora? Nessun problema! Questa guida è pensata appositamente per chi parte da zero con <strong>itaobuy sheet</strong>. Ti accompagneremo passo dopo passo.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Concetti Base</SectionHeading>
        <TextParagraph>Un foglio di calcolo è composto da righe e colonne. Ogni intersezione si chiama &quot;cella&quot;. In ogni cella puoi inserire testo, numeri o formule. Itaobuy sheet rende tutto questo semplice con un&apos;interfaccia colorata e aiuti contestuali in italiano.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>Prime Operazioni</SectionHeading>
        <TextParagraph>Inizia creando una semplice lista della spesa o un budget mensile. Impara a usare le formule base come SOMMA() e MEDIA(). Sperimenta con i colori e la formattazione. In pochi minuti ti sentirai già a tuo agio con itaobuy spreadsheet.</TextParagraph>
      </section>
    </>
  );
}

function VantaggiContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>10 Vantaggi di Itaobuy Sheet</SectionHeading>
        <TextParagraph>Scopri perché sempre più utenti italiani scelgono <strong>itaobuy sheet</strong> per la gestione dei propri dati. Ecco i 10 vantaggi principali di itaobuy spreadsheet.</TextParagraph>
      </section>
      <section className="mb-12">
        <div className="space-y-5">
          {[
            { num: '01', title: 'Interfaccia moderna e intuitiva', desc: 'Design pulito che riduce la curva di apprendimento.' },
            { num: '02', title: 'Accesso da qualsiasi dispositivo', desc: 'Piattaforma cloud accessibile da PC, tablet e smartphone.' },
            { num: '03', title: 'Collaborazione in tempo reale', desc: 'Più persone possono lavorare simultaneamente sullo stesso foglio.' },
            { num: '04', title: 'Template professionali', desc: 'Decine di modelli pronti per diverse esigenze lavorative.' },
            { num: '05', title: 'Automazioni no-code', desc: 'Crea flussi di lavoro automatici senza scrivere una riga di codice.' },
            { num: '06', title: 'Sicurezza enterprise', desc: 'Crittografia avanzata e backup automatici dei tuoi dati.' },
            { num: '07', title: 'Supporto in italiano', desc: 'Assistenza clienti e documentazione nella tua lingua.' },
            { num: '08', title: 'Prezzo competitivo', desc: 'Piani flessibili adatti a privati, professionisti e aziende.' },
            { num: '09', title: 'Aggiornamenti costanti', desc: 'Nuove funzionalità aggiunte regolarmente in base al feedback degli utenti.' },
            { num: '10', title: 'Community attiva', desc: 'Forum e gruppi di utenti dove scambiare consigli e template.' },
          ].map((v) => (
            <div key={v.num} className="flex gap-4">
              <span className="text-2xl font-bold text-primary-400 font-heading flex-shrink-0">{v.num}</span>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 font-label">{v.title}</h3>
                <p className="text-xs text-foreground-600 font-body mt-1">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function PrezzoContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Quanto Costa Itaobuy Sheet? Guida ai Prezzi 2026</SectionHeading>
        <TextParagraph>Analizziamo nel dettaglio i prezzi e i piani disponibili per <strong>itaobuy sheet</strong>. Scopri quale piano fa al caso tuo in base alle tue esigenze e al tuo budget.</TextParagraph>
      </section>
      <section className="mb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { name: 'Base', price: 'Gratuito', features: ['Fino a 10 fogli', 'Template base', 'Archiviazione 1GB', 'Supporto email'], highlight: false },
            { name: 'Pro', price: '€9.99/mese', features: ['Fogli illimitati', 'Tutti i template', 'Archiviazione 50GB', 'Automazioni avanzate', 'Supporto prioritario'], highlight: true },
            { name: 'Business', price: '€24.99/mese', features: ['Tutto il piano Pro', 'Multi-utente', 'Archiviazione 500GB', 'API access', 'Supporto dedicato 24/7'], highlight: false },
          ].map((plan) => (
            <div key={plan.name} className={`rounded-2xl border p-6 ${plan.highlight ? 'border-primary-300 bg-primary-50/50' : 'border-background-200/60 bg-background-50'}`}>
              <h3 className="text-lg font-bold text-foreground-900 font-heading">{plan.name}</h3>
              <p className="text-2xl font-bold text-primary-600 font-heading mt-2">{plan.price}</p>
              <ul className="mt-4 space-y-2">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs text-foreground-600 font-body">
                    <i className="ri-check-line text-primary-500 text-sm"></i>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function TemplateContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>I Migliori Template per Itaobuy Sheet</SectionHeading>
        <TextParagraph>Uno dei punti di forza di <strong>itaobuy sheet</strong> è la ricca libreria di template pronti all&apos;uso. Ecco i modelli più utili divisi per categoria.</TextParagraph>
      </section>
      <section className="mb-12">
        <div className="space-y-4">
          {[
            { cat: 'Business', items: 'Gestione inventario, Report vendite, Piano marketing, Analisi competitor, Budget aziendale' },
            { cat: 'Finanza Personale', items: 'Budget mensile, Tracciamento spese, Piano risparmio, Gestione debiti, Calcolo investimenti' },
            { cat: 'Progetti', items: 'Gantt chart, Gestione task, Timeline progetto, Allocazione risorse, Monitoraggio milestone' },
            { cat: 'Istruzione', items: 'Piano studi, Calcolo voti, Gestione lezioni, Bibliografia, Report ricerca' },
          ].map((c) => (
            <div key={c.cat} className="bg-background-50 rounded-2xl border border-background-200/60 p-5">
              <h3 className="text-sm font-semibold text-foreground-900 font-label mb-2">{c.cat}</h3>
              <p className="text-xs text-foreground-600 font-body">{c.items}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function GuidaAcquistoContent() {
  return (
    <>
      <section className="mb-12">
        <SectionHeading>Guida all&apos;Acquisto: Cosa Valutare Prima di Comprare</SectionHeading>
        <TextParagraph>Stai pensando di acquistare <strong>itaobuy sheet</strong>? Ecco tutto quello che devi sapere prima di prendere una decisione: funzionalità chiave, prezzi, alternative e consigli pratici per ottenere il massimo dal tuo investimento.</TextParagraph>
      </section>
      <section className="mb-12">
        <SectionHeading>5 Cose da Valutare</SectionHeading>
        <div className="space-y-5">
          {[
            { title: 'Le tue esigenze reali', desc: 'Prima di acquistare, identifica chiaramente cosa ti serve. Hai bisogno di collaborazione in tempo reale? Automazioni? Template specifici? Fai una lista.' },
            { title: 'Il budget disponibile', desc: 'Itaobuy sheet offre piani per tutte le tasche. Il piano gratuito è ottimo per iniziare, mentre i piani premium offrono funzionalità aggiuntive.' },
            { title: 'La curva di apprendimento', desc: 'Quanto tempo sei disposto a investire nell\'apprendimento? Itaobuy sheet ha una curva di apprendimento bassa, ideale se vuoi risultati immediati.' },
            { title: 'Integrazioni necessarie', desc: 'Verifica che itaobuy spreadsheet si integri con gli altri strumenti che usi quotidianamente.' },
            { title: 'Supporto e community', desc: 'La disponibilità di supporto in italiano e una community attiva possono fare la differenza quando incontri difficoltà.' },
          ].map((item, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary-600 font-label">{i + 1}</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 font-label">{item.title}</h3>
                <p className="text-xs text-foreground-600 font-body mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}